import DayPlanTrackingActionButton from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/ActionButton";
import { getStatus } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/utils";
import { DayPlanTrackingType } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/types";
import { getPhoneNumberWithoutNOPrefix } from "features/Header/PuzzelTelephone/utils";
import { ComponentProps, FC } from "react";
import { CallButton } from "shared/components/buttons/CallButton";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Badge, Grid, Stack, Text } from "shared/components/Spor";
import { formatDayDateString } from "shared/utils/dateTimeUtils";
import { css, useTheme } from "styled-components";

const getBackgroundColor = (
  severity: string,
): ComponentProps<typeof Grid>["color"] => {
  switch (severity) {
    case "WARN":
      return "semantics.colorWarningFill";
    case "ERROR":
      return "semantics.colorAlarmFill";
    case "OK":
      return "semantics.colorSuccessFill";
    default:
      return "inherit";
  }
};

const getHoverBackgroundColor = (
  severity: string,
): ComponentProps<typeof Grid>["color"] => {
  switch (severity) {
    case "WARN":
      return "semantics.colorWarningFillPulse";
    case "ERROR":
      return "semantics.colorAlarmFillPulse";
    case "OK":
      return "semantics.colorSuccessFillPulse";
    default:
      return "inherit";
  }
};

type TableProps = {
  filteredDayPlanTrackings: DayPlanTrackingType[];
  onRowClick: (dayPlanTrackingType: DayPlanTrackingType) => void;
};

export const TableContent: FC<TableProps> = ({
  filteredDayPlanTrackings,
  onRowClick,
}) => {
  const theme = useTheme();

  return filteredDayPlanTrackings.length > 0 ? (
    filteredDayPlanTrackings.map((it, index) => {
      const status = getStatus(it.status);
      const backgroundColor = getBackgroundColor(status.severity);
      const hoverBackgroundColor = getHoverBackgroundColor(status.severity);
      return (
        <Grid
          key={`${it.employeeId}-${it.dayPlanDate}`}
          cursor="pointer"
          gridColumn="1 / -1"
          gridTemplateColumns="subgrid"
          backgroundColor={backgroundColor}
          _hover={{ backgroundColor: hoverBackgroundColor }}
          border="1px solid"
          borderColor="outline.disabled"
          borderTop="none"
          borderBottomRadius={
            index === filteredDayPlanTrackings.length - 1 ? "xs" : undefined
          }
          transition="all 200ms ease-in-out"
          onClick={() => onRowClick(it)}
        >
          <Text px="3" py="1.5">
            <Badge fontSize="14px" lineHeight="21px" colorPalette="grey">
              {status.text}
            </Badge>
          </Text>
          <Text variant="sm" px="3" py="1.5">
            {it.companyCode}
          </Text>
          <Text variant="sm" px="3" py="1.5">
            {formatDayDateString(it.lastUpdatedAt)}
          </Text>
          <Text variant="sm" px="3" py="1.5">
            {formatDayDateString(it.dutyStart)}
          </Text>
          <Text variant="sm" px="3" py="1.5">
            {it.fullName ? `${it.fullName} (${it.employeeId})` : it.employeeId}
          </Text>
          <Text
            variant="sm"
            px="3"
            py="1.5"
          >{`${it.isConductor ? "K" : ""} ${it.isDriver ? "L" : ""}`}</Text>
          <Text
            variant="sm"
            px="3"
            py="1.5"
          >{`${it.dutyId} ${it.deletion ? "(slettet)" : ""}`}</Text>

          <Stack px="3" py="1.5">
            {it.phoneNumber && (
              <CallButton
                phoneNumber={getPhoneNumberWithoutNOPrefix(it.phoneNumber)}
                size="xs"
              />
            )}
          </Stack>
          <Stack px="3" py="1.5">
            <DayPlanTrackingActionButton
              status={it.status}
              employeeId={it.employeeId}
            />
          </Stack>
        </Grid>
      );
    })
  ) : (
    <Stack
      gridColumn="1 / -1"
      borderBottomRadius="xs"
      backgroundColor="semantics.colorBackgroundSecondary"
      border={`1px solid ${theme.colorOutlineSecondary}`}
      borderTop="none"
    >
      <NoDataMessage
        panelStyle={css`
          border-radius: 0;
          background-color: transparent;
        `}
        reason="Fant ingen tjenestevarslinger som matcher aktivt filter"
      />
    </Stack>
  );
};
